import axios from "axios";

// Same-origin by default (works on any custom domain: /api is routed to the backend by the same ingress).
// REACT_APP_BACKEND_URL is used only for local development servers.
const LOCAL = typeof window === "undefined" || ["localhost", "127.0.0.1"].includes(window.location.hostname);
export const BACKEND_URL = LOCAL ? process.env.REACT_APP_BACKEND_URL : window.location.origin;
export const API = `${BACKEND_URL}/api`;

const getBrowserId = () => {
  let id = localStorage.getItem("frozer_bid");
  if (!id) {
    id = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-bid`;
    localStorage.setItem("frozer_bid", id);
  }
  return id;
};

// Tokens are also kept client-side as a Bearer fallback for browsers that drop cookies.
const TK = "frozer_tokens";
const readTokens = () => { try { return JSON.parse(localStorage.getItem(TK)) || {}; } catch { return {}; } };
export const saveTokens = (data) => {
  if (!data) return;
  const cur = readTokens();
  ["access_token", "refresh_token", "media_token"].forEach((k) => { if (data[k]) cur[k] = data[k]; });
  localStorage.setItem(TK, JSON.stringify(cur));
};
export const clearTokens = () => localStorage.removeItem(TK);

export const http = axios.create({ baseURL: API, withCredentials: true });
http.interceptors.request.use((cfg) => {
  cfg.headers["X-Browser-Id"] = getBrowserId();
  const { access_token: at } = readTokens();
  if (at) cfg.headers.Authorization = `Bearer ${at}`;
  return cfg;
});
const doRefresh = async () => {
  const { data } = await http.post("/auth/refresh", { refresh_token: readTokens().refresh_token || null });
  saveTokens(data);
};
let onUnauthorized = () => {};
export const setUnauthorizedHandler = (fn) => { onUnauthorized = fn; };
http.interceptors.response.use(undefined, async (error) => {
  const cfg = error.config || {};
  const url = cfg.url || "";
  if (error.response?.status === 401 && !cfg._retry && !url.startsWith("/auth/")) {
    cfg._retry = true;
    try {
      await doRefresh();
      return http(cfg);
    } catch {
      onUnauthorized();
    }
  }
  return Promise.reject(error);
});

const withTokens = async (p) => { const { data } = await p; saveTokens(data); return data; };

export const authApi = {
  refreshMe: async () => {
    const get = async () => { const { data } = await http.get("/auth/me"); saveTokens(data); return data; };
    try { return await get(); } catch (e) {
      if (e.response?.status !== 401) throw e;
      await doRefresh();
      return get();
    }
  },
  login: async (email, password) => withTokens(http.post("/auth/login", { email, password })),
  register: async (email, password, name) => withTokens(http.post("/auth/register", { email, password, name })),
  google: async (sessionId) => withTokens(http.post("/auth/google", { session_id: sessionId })),
  logout: async () => { try { await http.post("/auth/logout"); } finally { clearTokens(); } },
  forgot: async (email, lang) => (await http.post("/auth/forgot-password", { email, lang })).data,
  reset: async (token, password) => (await http.post("/auth/reset-password", { token, password })).data,
  history: async () => (await http.get("/credits/history")).data,
  packs: async () => (await http.get("/payments/packs")).data,
  checkout: async (packId) => (await http.post("/payments/checkout", { pack_id: packId, origin_url: window.location.origin })).data,
  payStatus: async (sid) => (await http.get(`/payments/status/${sid}`)).data,
  payCancel: async (tx) => (await http.post(`/payments/cancel/${tx}`)).data,
  purchases: async () => (await http.get("/payments/history")).data,
};

// Media token lets <img>/<video> load private files even when cookies are blocked (user-bound, 24 h).
const withMedia = (url, extra = "") => {
  const mt = readTokens().media_token;
  const q = [extra, mt ? `mt=${encodeURIComponent(mt)}` : ""].filter(Boolean).join("&");
  return q ? `${url}${url.includes("?") ? "&" : "?"}${q}` : url;
};
export const fileUrl = (f, download = false) => (f ? withMedia(`${BACKEND_URL}${f.url}`, download ? "download=1" : "") : "");
export const posterUrl = (f) => (f?.poster_url ? withMedia(`${BACKEND_URL}${f.poster_url}`) : "");

export const errDetail = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  if (e?.response) return `Erreur serveur (${e.response.status})`;
  if (e?.request) return "Serveur injoignable (réseau ou blocage du navigateur)";
  return e?.message || "Erreur inconnue";
};

export const uploadFile = async (file, onProgress) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await http.post("/uploads", fd, {
    onUploadProgress: (p) => onProgress?.(p.total ? Math.round((p.loaded / p.total) * 100) : 0),
  });
  return data;
};

export const api = {
  library: async () => (await http.get("/library")).data,
  createProject: async (fileId) => (await http.post("/projects", { file_id: fileId })).data,
  project: async (id) => (await http.get(`/projects/${id}`)).data,
  projects: async () => (await http.get("/projects")).data,
  setCurrent: async (id, index) => (await http.post(`/projects/${id}/current`, { index })).data,
  undo: async (id) => (await http.post(`/projects/${id}/undo`)).data,
  remove: async (id) => (await http.delete(`/projects/${id}`)).data,
  createJob: async (payload) => (await http.post("/jobs", payload)).data,
};

export const ACTIVE = ["queued", "running", "chunk"];
